import { FilterMatchMode } from "primereact/api"
import { Button } from "primereact/button"
import { Column } from "primereact/column"
import { DataTable } from "primereact/datatable"
import { InputText } from "primereact/inputtext"
import { MouseEvent, useEffect, useState } from "react"
import { NavigateFunction, useNavigate } from "react-router-dom"
import useFetchGroupList from "./useFetchGroupList"
import { Tag } from "primereact/tag"
import ActiveDeactiveDialogConfirmation from "components/ActiveDeactiveDialogConfirmation"
import { ToastContent, toast } from "react-toastify"
import { activeInactiveGroupService, addGroupService } from "services/groupService"
import { PERMISSION_DENIED_MESSAGE } from "../../constants/index"
import { useDispatch, useSelector } from "react-redux"
import { handleLoader, handleNotFound } from "redux/counterSlice"
import { IconField } from "primereact/iconfield"
import { InputIcon } from "primereact/inputicon"
import { datatableEdit, datatableEditHover } from "assets/icons"
import { Sidebar } from "primereact/sidebar"
import { InputTextarea } from "primereact/inputtextarea"
import { useForm } from "react-hook-form"

const Groups = () => {
	const {
		register,
		handleSubmit,
		formState: { errors },
		setValue,
		clearErrors
	} = useForm()
	const permissions = useSelector((state: any) => state.persistent.group_permissions);
	const [groupList, fetchGroupList] = useFetchGroupList()
	const [globalFilterValue, setGlobalFilterValue] = useState("")
	const [filters, setFilters] = useState<{ [s: string]: any }>()
	const [isActiveShouldBeLabel, setIsActiveShouldBeLabel] =
		useState<boolean>(false)
	const [selectedGroups, setSelectedGroups] = useState<{
		for_active: boolean
		group_id: Array<string>
	}>({
		for_active: true,
		group_id: [],
	})
	const dispatch = useDispatch();
	const [selectedProducts, setSelectedProducts] =
		useState<Record<string, any>>()
	const [first, setFirst] = useState(0); // State to control the first page
	const [sortField, setSortField] = useState(""); // State to control the first page
	const [sortOrder, setSortOrder] = useState<any>(""); // State to control the first page
	const [rowsPerPage, setRowsPerPage] = useState(5); // State to control the rows per page

	const navigate = useNavigate()

	const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setFirst(0);
		const value = e?.target?.value
		let _filters = { ...filters }

		_filters["global"]!.value = value

		setFilters(_filters)
		setGlobalFilterValue(value)
	}
	const handleSort = (event: any) => {
		setSortField(event.sortField)
		setSortOrder(event.sortOrder)
		setFirst(0);  //Move to page 1
		// dispatch(setFirstVoice(0)); // Move to page 1
	};

	const initFilters = () => {
		setFilters({
			global: { value: null, matchMode: FilterMatchMode.CONTAINS },
		})
		setGlobalFilterValue("")
	}

	const getSeverity = (status: string) => {
		switch (status) {
			case "Active":
				return "success"

			case "Inactive":
				return "warning"
		}
	}

	const activeInactiveLabelHandler = (
		e: MouseEvent<HTMLSpanElement, globalThis.MouseEvent>,
		{ id, is_active }: { id: string; is_active: boolean }
	) => {
		e.stopPropagation()
		setSelectedGroups((prevState) => ({
			for_active: !is_active,
			group_id: [...prevState.group_id, id],
		}))
		setIsActiveShouldBeLabel(!is_active)
	}

	const onClear = () => {
		setSelectedProducts(undefined)
		setSelectedGroups({ for_active: true, group_id: [] })
		setIsActiveShouldBeLabel(false)
	}

	const activeInactiveGroupListHandler = async (isActive: boolean) => {
		if (selectedProducts?.length > 0) {
			selectedProducts?.forEach((element: any) => {
				setSelectedGroups((prevState: any) => ({
					...prevState,
					for_active: isActive,
					group_id: [...prevState.group_id, element.id],
				}))
			})
			setIsActiveShouldBeLabel(isActive)
		} else {
			toast.error("Please select at least one group to update the status", {
				style: {
					backgroundColor: '#F1F1F1', // Grey background
				}
			})
		}
	}

	const deleteUsers = async () => {
		dispatch(handleLoader(true));
		try {
			const { response, error } = await activeInactiveGroupService(
				selectedGroups
			)
			if (error) {
				throw error
			}
			if (response?.error) {
				throw response?.error
			}
			await fetchGroupList()
			dispatch(handleLoader(false));
			toast.success(response?.message, {
				style: {
					backgroundColor: '#F1F1F1', // Grey background
				}
			})
			onClear()
		} catch (error) {
			dispatch(handleLoader(false));
			toast.error(error as ToastContent<unknown>, {
				style: {
					backgroundColor: '#F1F1F1', // Grey background
				}
			})
		}
	}

	const getFormErrorMessage = (name: string) => {
		return (
			errors[name] && (
				<small className="p-error">{errors[name]?.message?.toString()}</small>
			)
		)
	}

	const onSubmit = async (data: { [x: string]: any }) => {
		if (permissions.add) {
			dispatch(handleLoader(true));
			try {
				const serviceBody = {
					group_name: data?.name,
					description: data?.description,
					created_by: window && window.localStorage.getItem("username"),
				}
				let { error, response } = await addGroupService(serviceBody)
				if (response?.error) {
					throw response?.error
				}
				if (error) {
					throw error
				}
				toast.success(response?.message, {
					style: {
						backgroundColor: '#F1F1F1', // Grey background
					}
				})
				setVisible(false);
				await fetchGroupList()
				dispatch(handleLoader(false));
				setValue("name", "")
				setValue("description", "")
			} catch (error) {
				dispatch(handleLoader(false));
				toast.error(error as ToastContent<unknown>, {
					style: {
						backgroundColor: '#F1F1F1', // Grey background
					}
				})
			}
		}
	}

	useEffect(() => {
		dispatch(handleNotFound(false))
		initFilters()
	}, [])

	const [visible, setVisible] = useState<boolean>(false);
	const customHeader = (
		<div className="flex align-items-center gap-2">
			Add Group
		</div>
	);
	const onCancel = () => {
		setVisible(false);
		setValue("name", "");
		setValue("description", "");
		clearErrors();
	}

	return (
		<>
			<div className="datatable-wrapper">
				<div className="p-4 content-wrapper">
					<div className="switch-section user-management-section">
						<div className='d-flex align-items-center justify-content-between flex-wrap mb-3 table-top-row'>
							<h4 className="mb-0">Group Management</h4>
							<div className="d-flex justify-content-between align-items-center table-right-row">
								<div className="d-flex align-items-center table-right-first">
									<div className="d-flex align-items-center table-search mr-3 mt-1 mb-1">
										<IconField iconPosition="right" className='user-input'>
											<InputIcon className="pi pi-search"></InputIcon>
											<InputText value={globalFilterValue}
												onChange={onGlobalFilterChange}
												placeholder="Search by group details" />
										</IconField>
										{/* <i className="pi pi-search" />
								<InputText
									value={globalFilterValue}
									onChange={onGlobalFilterChange}
									placeholder="Enter group name"
								/> */}
									</div>
									{permissions.add &&
										<Button
											label="Add Group"
											outlined icon="pi pi-plus"
											className='outline-icon-btn mr-3 mt-1 mb-1'
											// onClick={async () => navigate("/admin/groups/add")}
											onClick={() => setVisible(true)}
										// disabled={!permissions.add}
										/>
									}
								</div>
								{permissions.delete &&
									<div className="table-right-end mt-1 mb-1">
										<Button
											className='outline-icon-btn grey-outline-btn active-btn mr-3' outlined label='Active'
											data-bs-toggle={permissions.delete && (selectedProducts && selectedProducts !== null && selectedProducts.length !== 0) && "modal"}
											data-bs-target={permissions.delete && "#active-deactive"}
											onClick={() => activeInactiveGroupListHandler(true)}
										></Button>
										<Button
											className='outline-icon-btn grey-outline-btn inactive-btn' outlined label='Inactive'
											data-bs-toggle={permissions.delete && (selectedProducts && selectedProducts !== null && selectedProducts.length !== 0) && "modal"}
											data-bs-target={permissions.delete && "#active-deactive"}
											onClick={() => activeInactiveGroupListHandler(false)}
										></Button>
									</div>
								}
							</div>
						</div>
						<DataTable
							selection={selectedProducts}
							selectionPageOnly
							onSelectionChange={(e) => setSelectedProducts(e.value)}
							paginator
							filters={filters}
							globalFilterFields={["name", "description"]}
							first={first}
							onPage={(e) => { setFirst(e.first); setRowsPerPage(e.rows) }}
							rows={rowsPerPage}
							sortField={sortField}
							sortOrder={sortOrder}
							sortMode="single"
							onSort={handleSort}
							rowsPerPageOptions={[5, 10, 25]}
							stripedRows
							value={groupList}
							tableStyle={{}}
							emptyMessage="No group records found"
						>
							<Column
								selectionMode="multiple"
								headerStyle={{ width: "3rem" }}
							></Column>
							<Column field="name" header="Group Name" sortable></Column>
							<Column
								field="description"
								header="Group Description"
								sortable
							></Column>
							<Column
								field="status"
								header="Status"
								showFilterMenu={false}
								filterMenuStyle={{ width: "14rem" }}
								body={(rowData) => (
									<Tag
										data-bs-toggle={permissions.delete && "modal"}
										data-bs-target={permissions.delete && "#active-deactive"}
										onClick={(e) => {
											permissions.delete ? activeInactiveLabelHandler(e, rowData) : toast.error(PERMISSION_DENIED_MESSAGE, {
												style: {
													backgroundColor: '#F1F1F1', // Grey background
												}
											})
										}}
										value={rowData.is_active ? "Active" : "Inactive"}
										severity={getSeverity(
											rowData.is_active ? "Active" : "Inactive"
										)}
										className={rowData.is_active ? 'status-tag active' : 'status-tag inactive'}
									/>
								)}
							/>
							<Column header="Action" body={ActionButton(navigate, permissions)}></Column>
						</DataTable>
					</div>
				</div>
			</div>
			<ActiveDeactiveDialogConfirmation
				active={isActiveShouldBeLabel}
				changeStatus={deleteUsers}
				onCancel={onClear}
			/>
			<Sidebar position="right" header={customHeader} visible={visible} onHide={onCancel} className='form-sidebar'>
				<form onSubmit={handleSubmit(onSubmit)}>
					<div className="form-sidebar-body">
						<div className='form-row'>
							<div className='form-col'>
								<label className="form-label">Name of Group<i>*</i></label>
								<InputText
									{...register("name", {
										required: {
											value: true,
											message: "Group Name is required",
										},
									})}
									placeholder="Name of Group"
								/>
								{getFormErrorMessage("name")}
							</div>
						</div>
						<div className='form-row'>
							<div className='form-col'>
								<label className="form-label">Description<i>*</i></label>
								<InputTextarea className="form-control p-inputtext-lg"
									{...register("description", {
										required: {
											value: true,
											message: "Description is required",
										},
									})}
									placeholder="Add group description here..."
								/>
								{getFormErrorMessage("description")}
							</div>
						</div>
					</div>
					<div className='form-sidebar-footer'>
						<Button className='grey-outline-btn btn-lg' type="button" onClick={onCancel} label='Cancel'></Button>
						<Button className='outline-active-btn btn-lg' type="submit" label='Save'></Button>
					</div>
				</form>
			</Sidebar>
		</>
	)
}

export default Groups
function ActionButton(navigate: NavigateFunction, permissions: any) {
	const [is_hover_Edit, setIs_hover_Edit] = useState<string>("");
	return (rowData: { [s: string]: string }) => {
		return (
			<div>
				<span
					onClick={() => permissions.edit ? navigate(`/admin/groups/${rowData.id}`) : toast.error(PERMISSION_DENIED_MESSAGE, {
						style: {
							backgroundColor: '#F1F1F1', // Grey background
						}
					})}
					onMouseEnter={() => setIs_hover_Edit(rowData.id)} onMouseLeave={() => setIs_hover_Edit("")}
				// disabled={!permissions.edit}
				>
					<img src={is_hover_Edit === rowData.id ? datatableEditHover : datatableEdit} alt='edit-icon' />
				</span>
				{/* <Button
                    className="pi pi-trash"
                    onClick={() => onDeleteClick(rowData.id)}
                ></Button> */}
			</div>
		)
	}
}
